/**
 * nfe-backend.ts
 * Serviço de persistência: encapsula todas as chamadas ao Supabase
 * relacionadas ao pipeline NF-e.
 *
 * Todas as operações respeitam o RLS do Supabase — o user_id é
 * inferido pelo JWT da sessão atual, nunca enviado pelo frontend.
 */
import { supabase } from "@/lib/supabase";
import type { NotaSimplificada, Divergencia } from "@/lib/fiscal-types";

// ──────────────────────────────────────────────────────────────────────
// TIPOS
// ──────────────────────────────────────────────────────────────────────

export interface BackendSaveResult {
  ok: boolean;
  saved?: number;
  updated?: number;
  errors?: number;
  auditoria?: number;
  status_counts?: { OK: number; MEDIA: number; CRITICA: number };
  error?: string;
}

export interface ReprocessResult {
  ok: boolean;
  reprocessadas?: number;
  corrigidas?: number;
  erros?: number;
  detalhes_erros?: string[];
  error?: string;
}

export interface CreateUserResult {
  ok: boolean;
  user_id?: string;
  email?: string;
  password_gerada?: string | null;
  message?: string;
  error?: string;
}

export interface UploadSessionRow {
  id: string;
  arquivo: string;
  tamanho_bytes: number;
  total_notas: number;
  total_divergencias: number;
  duracao_ms: number;
  status: "success" | "error";
  criado_em: string;
}

// ──────────────────────────────────────────────────────────────────────
// 1. SALVAR RESULTADO DO PIPELINE NO BACKEND
// Chamado em background após pipeline local completar (não bloqueia UI)
// ──────────────────────────────────────────────────────────────────────
export async function saveSessionToBackend(params: {
  notas: NotaSimplificada[];
  divergencias: Divergencia[];
  session: {
    fileName: string;
    fileSize: number;
    duracaoMs: number;
  };
}): Promise<BackendSaveResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { ok: false, error: "not_authenticated" };

  try {
    const { data, error } = await supabase.functions.invoke("process-nfe", {
      body: {
        notas: params.notas,
        session: {
          fileName:           params.session.fileName,
          fileSize:           params.session.fileSize,
          duracaoMs:          params.session.duracaoMs,
          totalDivergencias:  params.divergencias.length,
        },
      },
    });

    if (error) return { ok: false, error: error.message };
    return data as BackendSaveResult;
  } catch (e: unknown) {
    return { ok: false, error: e instanceof Error ? e.message : "unknown_error" };
  }
}

// ──────────────────────────────────────────────────────────────────────
// 2. REPROCESSAR NOTAS (botão Corrigir Divergências)
// Chama reprocess-nfe que: busca no banco → revalida vNF → corrige → registra
// ──────────────────────────────────────────────────────────────────────
export async function reprocessNfe(params: {
  chaves?: string[];
  todas?: boolean;
}): Promise<ReprocessResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { ok: false, error: "not_authenticated" };

  try {
    const { data, error } = await supabase.functions.invoke("reprocess-nfe", {
      body: params,
    });

    if (error) return { ok: false, error: error.message };
    return data as ReprocessResult;
  } catch (e: unknown) {
    return { ok: false, error: e instanceof Error ? e.message : "unknown_error" };
  }
}

// ──────────────────────────────────────────────────────────────────────
// 3. CRIAR USUÁRIO (apenas admins)
// ──────────────────────────────────────────────────────────────────────
export async function createUserAdmin(params: {
  email: string;
  password?: string;
  display_name?: string;
  role?: "admin" | "user";
  email_confirm?: boolean;
  invite?: string;
}): Promise<CreateUserResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { ok: false, error: "not_authenticated" };

  try {
    const { data, error } = await supabase.functions.invoke("create-user", {
      body: params,
    });

    if (error) return { ok: false, error: error.message };
    return data as CreateUserResult;
  } catch (e: unknown) {
    return { ok: false, error: e instanceof Error ? e.message : "unknown_error" };
  }
}

// ──────────────────────────────────────────────────────────────────────
// 4. BUSCAR HISTÓRICO DE UPLOADS DO BANCO
// ──────────────────────────────────────────────────────────────────────
export async function loadUploadSessions(): Promise<UploadSessionRow[]> {
  const { data, error } = await supabase
    .from("upload_sessions")
    .select("id,arquivo,tamanho_bytes,total_notas,total_divergencias,duracao_ms,status,criado_em")
    .order("criado_em", { ascending: false })
    .limit(50);

  if (error) {
    console.warn("loadUploadSessions:", error.message);
    return [];
  }
  return (data ?? []) as UploadSessionRow[];
}

// ──────────────────────────────────────────────────────────────────────
// 5. BUSCAR RESULTADO DE AUDITORIA DO BANCO
// ──────────────────────────────────────────────────────────────────────
export async function loadAuditoria(chaves?: string[]) {
  let q = supabase
    .from("nfe_auditoria")
    .select("nfe_chave,vnf_xml,vnf_calculado,diferenca,tolerancia,status_vnf,score,processado_em,fonte")
    .order("processado_em", { ascending: false })
    .limit(200);

  if (chaves?.length) q = q.in("nfe_chave", chaves);

  const { data, error } = await q;
  if (error) { console.warn("loadAuditoria:", error.message); return []; }
  return data ?? [];
}

// ──────────────────────────────────────────────────────────────────────
// 6. BUSCAR CORREÇÕES APLICADAS
// ──────────────────────────────────────────────────────────────────────
export async function loadCorrecoes(chaves?: string[]) {
  let q = supabase
    .from("nfe_correcoes")
    .select("nfe_chave,campo,valor_original,valor_corrigido,tipo_correcao,descricao,aplicado_em")
    .order("aplicado_em", { ascending: false })
    .limit(200);

  if (chaves?.length) q = q.in("nfe_chave", chaves);

  const { data, error } = await q;
  if (error) { console.warn("loadCorrecoes:", error.message); return []; }
  return data ?? [];
}

// ──────────────────────────────────────────────────────────────────────
// 7. LISTAR USUÁRIOS (admin)
// ──────────────────────────────────────────────────────────────────────
export async function loadProfiles() {
  const [{ data: profiles, error: pe }, { data: roles, error: re }] = await Promise.all([
    supabase
      .from("profiles")
      .select("id,email,display_name,is_active,created_at")
      .order("created_at", { ascending: false }),
    supabase.from("user_roles").select("user_id,role"),
  ]);

  if (pe || re) {
    console.warn("loadProfiles:", pe?.message ?? re?.message);
    return [];
  }

  const byUser = new Map<string, string[]>();
  (roles ?? []).forEach((r: any) => {
    const arr = byUser.get(r.user_id) ?? [];
    arr.push(r.role);
    byUser.set(r.user_id, arr);
  });

  return (profiles ?? []).map((p: any) => ({ ...p, roles: byUser.get(p.id) ?? [] }));
}
